import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar } from '../components/ListControls';
import {
  CLIENT_MARGIN_ACCOUNTS,
  CLIENT_FACILITIES,
  CLIENT_WALLET_BALANCES,
} from '../data/mockAdapter';

interface Facility {
  id: string;
  name: string;
  collateralTokens: string[];
  settlementTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  tvl: string;
  borrowed: string;
  status: 'active' | 'paused';
}

interface MarginAccount {
  id: string;
  facilityId: string;
  facilityName: string;
  settlementToken: string;
  collateralTokens: string;
  collateralAmount: string;
  collateralValue: string;
  borrowedAmount: string;
  borrowedValue: string;
  interestAccrued: string;
  healthFactor: string;
  status: 'healthy' | 'at-risk' | 'liquidated';
}

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility) => ({
  id: facility.id,
  name: facility.name,
  collateralTokens: facility.collateralTokens,
  settlementTokens: facility.settlementTokens,
  interestRate: facility.interestRate,
  maxLTV: facility.maxLTV,
  liquidationThreshold: facility.liquidationThreshold,
  utilization: facility.utilization,
  tvl: facility.tvl,
  borrowed: facility.borrowed,
  status: facility.status,
}));

const MARGIN_ACCOUNTS: MarginAccount[] = CLIENT_MARGIN_ACCOUNTS.map((account) => ({
  id: account.accountId,
  facilityId: account.facilityId,
  facilityName: account.facilityName,
  settlementToken: account.settlementToken,
  collateralTokens: account.collateralTokenText,
  collateralAmount: account.collateralAmountText,
  collateralValue: account.collateralValue,
  borrowedAmount: account.borrowedAmount,
  borrowedValue: account.borrowedValue,
  interestAccrued: account.interestAccrued,
  healthFactor: account.healthFactor,
  status: account.status,
}));

const WALLET_BALANCES: Record<string, string> = CLIENT_WALLET_BALANCES;

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

function parseAmount(s: string): number {
  return parseFloat(s.replace(/[^0-9.]/g, '')) || 0;
}

function getHFColor(hf: number): string {
  if (hf >= 1.5) return 'text-green-600';
  if (hf >= 1.2) return 'text-yellow-600';
  return 'text-red-600';
}

function getStatusColor(status: MarginAccount['status']): string {
  if (status === 'healthy') return 'bg-green-100 text-green-700';
  if (status === 'at-risk') return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

// Resolve query string → accountId. Prefer ?accountId; fall back to ?facilityId
// by picking the healthiest account in that facility (keeps old links working).
function resolveAccountIdFromParams(params: URLSearchParams): string | null {
  const explicit = params.get('accountId');
  if (explicit) return explicit;
  const fid = params.get('facilityId');
  if (!fid) return null;
  const candidates = MARGIN_ACCOUNTS.filter((a) => a.facilityId === fid);
  if (candidates.length === 0) return null;
  const healthy = candidates.find((a) => a.status === 'healthy');
  return (healthy ?? candidates[0]).id;
}

export function RepayPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    () => resolveAccountIdFromParams(searchParams),
  );
  const [amount, setAmount] = useState('');
  const [isRepaying, setIsRepaying] = useState(false);
  const [search, setSearch] = useState('');

  const account = MARGIN_ACCOUNTS.find((a) => a.id === selectedAccountId);
  const facility = account ? FACILITIES.find((v) => v.id === account.facilityId) : undefined;

  const borrowedNumeric = account ? parseAmount(account.borrowedAmount) : 0;
  const interestNumeric = account ? parseAmount(account.interestAccrued) : 0;
  const totalDebt = borrowedNumeric + interestNumeric;
  const parsedAmount = parseFloat(amount) || 0;

  const token = account?.settlementToken ?? '';
  const walletBal = WALLET_BALANCES[token] ?? '0';
  const walletNumeric = parseFloat(walletBal.replace(/,/g, '')) || 0;
  const maxRepay = Math.min(walletNumeric > 0 ? walletNumeric : totalDebt, totalDebt);

  const currentHF = account ? parseFloat(account.healthFactor) : 0;
  const newHF =
    totalDebt > 0 && parsedAmount > 0
      ? parsedAmount >= totalDebt
        ? Infinity
        : currentHF * (totalDebt / (totalDebt - parsedAmount))
      : currentHF;
  const displayNewHF = newHF === Infinity ? '∞' : Math.min(newHF, 999).toFixed(2);

  const filteredAccounts = useMemo(
    () =>
      MARGIN_ACCOUNTS.filter((a) => {
        const q = search.toLowerCase();
        return a.facilityName.toLowerCase().includes(q) || a.collateralTokens.toLowerCase().includes(q);
      }),
    [search],
  );

  function selectAccount(accountId: string) {
    setSelectedAccountId(accountId);
    setSearchParams({ accountId });
    setAmount('');
  }

  function goBack() {
    setSelectedAccountId(null);
    setSearchParams({});
    setAmount('');
  }

  function handleMax() {
    setAmount(maxRepay.toString());
  }

  function handleRepay() {
    if (parsedAmount <= 0 || parsedAmount > totalDebt) return;
    setIsRepaying(true);
    setTimeout(() => {
      setIsRepaying(false);
      setAmount('');
      toast.success('Repayment submitted successfully');
    }, 2000);
  }

  if (account && facility) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Positions
        </button>

        <Card>
          <CardHeader className="p-6">
            <CardTitle>Repay {token}</CardTitle>
            <CardDescription>Repay debt on your margin account ({facility.name})</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Amount to repay</label>
                <span className="text-sm text-muted-foreground">
                  Balance: {walletBal} {token}
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className={`${INPUT_CLASS} pr-24`}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">{token}</span>
                  <button
                    type="button"
                    onClick={handleMax}
                    className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-2">
              <h4 className="text-sm font-medium">Outstanding Debt</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Principal</span>
                  <span>
                    {account.borrowedAmount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Interest Accrued</span>
                  <span className="text-yellow-600">{account.interestAccrued}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
                  <span>Total Owed</span>
                  <span>
                    {totalDebt.toLocaleString(undefined, { maximumFractionDigits: 2 })}{' '}
                    {token}
                  </span>
                </div>
              </div>
            </div>

            {parsedAmount > 0 && (
              <div className="rounded-lg border border-border p-4 space-y-3">
                <h4 className="text-sm font-medium">Health Factor Impact</h4>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex-1 text-center">
                    <p className="text-xs text-muted-foreground">Current</p>
                    <p className={`text-xl font-bold ${getHFColor(currentHF)}`}>
                      {currentHF.toFixed(2)}
                    </p>
                  </div>
                  <span className="text-muted-foreground text-lg">→</span>
                  <div className="flex-1 text-center">
                    <p className="text-xs text-muted-foreground">After Repayment</p>
                    <p
                      className={`text-xl font-bold ${
                        newHF === Infinity ? 'text-green-600' : getHFColor(newHF)
                      }`}
                    >
                      {displayNewHF}
                    </p>
                  </div>
                </div>
                {parsedAmount >= totalDebt && (
                  <p className="text-xs text-green-600">
                    Full repayment will close this debt position entirely.
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={goBack}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={isRepaying || parsedAmount <= 0 || parsedAmount > totalDebt}
                onClick={handleRepay}
              >
                {isRepaying ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Repaying...
                  </>
                ) : (
                  `Repay ${token}`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Repay Debt</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Repay borrowed amounts on your margin accounts to improve your health factor.
        </p>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search margin accounts..." className="sm:w-64" />

      {filteredAccounts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No margin accounts found matching your search.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredAccounts.map((acct) => {
            const v = FACILITIES.find((x) => x.id === acct.facilityId);
            if (!v) return null;

            const borrowed = parseAmount(acct.borrowedAmount);
            const interest = parseAmount(acct.interestAccrued);
            const total = borrowed + interest;

            return (
              <Card key={acct.id}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{acct.facilityName}</h3>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(acct.status)}`}
                    >
                      {acct.status}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Settlement: {acct.settlementToken}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Borrowed</span>
                      <p className="font-semibold">{acct.borrowedAmount}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Interest</span>
                      <p className="font-semibold">{acct.interestAccrued}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Owed</span>
                      <p className="font-semibold text-amber-600">
                        {total.toLocaleString(undefined, { maximumFractionDigits: 2 })} {acct.settlementToken}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Health Factor</span>
                      <p className="font-semibold">{acct.healthFactor}</p>
                    </div>
                  </div>

                  <Button className="w-full" onClick={() => selectAccount(acct.id)}>
                    Repay →
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
